import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { OnboardingStep } from '../types/onboardingTypes'
import type { IdentitySubStep, KycSession } from '../types/kycTypes'
import type { SourceOfFundsSubmitData } from '../types/sourceOfFundsTypes'

// Client-only state for the sign-up + post-account onboarding wizard.
//
// `flowSteps` and `kycSession` are mutation-derived but live here because
// they need to persist across the SignUp -> wizard navigation. Holding
// them in component state would force a re-call of `startOnboarding` /
// `createKycSession` after navigation, which is not idempotent server-side.
//
// Wizard state (`allSteps`, `currentStep`, `identitySubStep`, `riskAnswers`,
// `sourceOfFundsAnswers`, `allowLeave`) lives here so per-step containers can advance
// the flow and the review step can pull accumulated draft answers without
// prop-drilling.

const emptySourceOfFundsAnswers = (): SourceOfFundsSubmitData => ({
  employmentStatus: '',
  sourceOfIncome: '',
  monthlyIncomeRange: '',
  netWorth: '',
  industry: '',
  countryOfIncomeSource: '',
  plannedInvestment: '',
  isPep: false,
})

interface WizardStep {
  stepKey: string
  stepLabel: string
}

// The setup body is long by design — it's the wizard's single state holder.
// Per `.claude/rules/wizard-modules.md`: "Wizard state (allSteps, currentStep,
// identitySubStep, riskAnswers, sofAnswers, kycSession, …) lives here so
// per-step containers can advance the flow and the review step can pull
// accumulated draft answers without prop-drilling." Splitting along those
// slices would create cross-store coordination via composables — more files,
// not less complexity. Audit #38 reviewed this and confirmed it canonical.

export const useOnboardingStore = defineStore('onboarding', () => {
  // SignUp flow ---------------------------------------------------------------
  const isEmirateId = ref<boolean | null>(null)
  // AUDIT-EXCEPTION §4: not server cache. The startOnboarding mutation seeds
  // the wizard's step machine via this list; the cache shape (`useCurrentStepsQuery`)
  // covers a different read surface. See wizard-modules.md.
  const flowSteps = ref<OnboardingStep[]>([])
  const onboardingStatus = ref<string>('')

  // KYC + post-account wizard --------------------------------------------------
  // AUDIT-EXCEPTION §4: derived wizard machine (sorted flowSteps + LOCAL_STEPS),
  // not server cache. Drives currentStepKey / totalSteps / `<component :is>` switch.
  const allSteps = ref<WizardStep[]>([])
  const currentStep = ref(1)
  const identitySubStep = ref<IdentitySubStep>('form')
  const kycSession = ref<KycSession | null>(null)
  const riskAnswers = ref<Record<string, string>>({})
  const sourceOfFundsAnswers = ref<SourceOfFundsSubmitData>(emptySourceOfFundsAnswers())

  // Cross-flow leave guard — flipped true when a container intentionally
  // navigates away (e.g. "Skip to dashboard"); the wizard shell's
  // onBeforeRouteLeave reads this to bypass its confirm prompt.
  const allowLeave = ref(false)

  // Computed -----------------------------------------------------------------
  const totalSteps = computed(() => allSteps.value.length)
  const currentStepKey = computed(() => allSteps.value[currentStep.value - 1]?.stepKey ?? null)

  // SignUp actions -----------------------------------------------------------
  function setEmirateId(value: boolean | null) {
    isEmirateId.value = value
  }

  function setFlowSteps(steps: OnboardingStep[], status: string) {
    flowSteps.value = steps
    onboardingStatus.value = status
  }

  // Wizard actions -----------------------------------------------------------
  function setAllSteps(steps: WizardStep[]) {
    allSteps.value = steps
  }

  function setCurrentStep(step: number) {
    currentStep.value = step
  }

  function nextStep() {
    if (currentStep.value < totalSteps.value) currentStep.value++
  }

  // Move the wizard to the first step whose key matches one of `keys`. Useful
  // for "Edit responses" style navigation back to a labelled step regardless
  // of its numeric position.
  function gotoStepKey(...keys: string[]) {
    for (const key of keys) {
      const idx = allSteps.value.findIndex((step) => step.stepKey === key)
      if (idx >= 0) {
        currentStep.value = idx + 1
        return
      }
    }
  }

  function setIdentitySubStep(value: IdentitySubStep) {
    identitySubStep.value = value
  }

  function setKycSession(session: KycSession | null) {
    kycSession.value = session
  }

  function setRiskAnswers(answers: Record<string, string>) {
    riskAnswers.value = answers
  }

  function setSourceOfFundsAnswers(answers: SourceOfFundsSubmitData) {
    sourceOfFundsAnswers.value = answers
  }

  function setAllowLeave(value: boolean) {
    allowLeave.value = value
  }

  function reset() {
    isEmirateId.value = null
    flowSteps.value = []
    onboardingStatus.value = ''
    allSteps.value = []
    currentStep.value = 1
    identitySubStep.value = 'form'
    kycSession.value = null
    riskAnswers.value = {}
    sourceOfFundsAnswers.value = emptySourceOfFundsAnswers()
    allowLeave.value = false
  }

  return {
    // signUp
    isEmirateId,
    flowSteps,
    onboardingStatus,
    setEmirateId,
    setFlowSteps,
    // wizard
    allSteps,
    currentStep,
    currentStepKey,
    totalSteps,
    identitySubStep,
    kycSession,
    riskAnswers,
    sourceOfFundsAnswers,
    allowLeave,
    setAllSteps,
    setCurrentStep,
    nextStep,
    gotoStepKey,
    setIdentitySubStep,
    setKycSession,
    setRiskAnswers,
    setSourceOfFundsAnswers,
    setAllowLeave,
    reset,
  }
})
